import { useEffect, useMemo, useState } from "react";
import {
  getCurrentSeason,
  getListCompetitionMatches,
  getListCompetitionParticipants,
  getListCompetitionStages,
  getListTournamentEvents,
  getListUsers,
  compareUsersByName,
} from "../api";
import { CompetitionStage, Match, Season, TournamentEvent, TournamentParticipant, User } from "../types";

// Carica e normalizza i dati del calendario globale (stagione corrente o quella
// scelta), filtrando match/partecipanti/stage agli eventi della stagione ed
// esponendo le mappe id->entità usate ovunque nella pagina.
export const useCalendarData = () => {
  const [season, setSeason] = useState<Season | null>(null);
  const [seasonUsers, setSeasonUsers] = useState<User[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  const [stages, setStages] = useState<CompetitionStage[]>([]);
  const [events, setEvents] = useState<TournamentEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadCalendar = async (selectedSeason?: Season) => {
    setIsLoading(true);
    try {
      const current = selectedSeason ?? (await getCurrentSeason());
      const [matchesData, participantsData, stagesData, eventsData, usersData] = await Promise.all([
        getListCompetitionMatches(),
        getListCompetitionParticipants(),
        getListCompetitionStages(),
        getListTournamentEvents({ seasonId: current.id }),
        getListUsers(current.id),
      ]);
      const eventIds = new Set(eventsData.map((eventItem) => eventItem.id));
      const seasonStages = stagesData.filter((stage) => eventIds.has(stage.event));
      const stageIds = new Set(seasonStages.map((stage) => stage.id));
      const seasonParticipants = participantsData.filter((participant) => eventIds.has(participant.event));
      const seasonMatches = matchesData.filter((match) => stageIds.has(match.stage));

      setSeason(current);
      setSeasonUsers([...usersData].sort(compareUsersByName));
      setMatches(seasonMatches);
      setParticipants(seasonParticipants);
      setStages(seasonStages);
      setEvents(eventsData);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const participantsById = useMemo(
    () => new Map(participants.map((participant) => [participant.id, participant])),
    [participants]
  );
  const stagesById = useMemo(() => new Map(stages.map((stage) => [stage.id, stage])), [stages]);
  const eventsById = useMemo(() => new Map(events.map((event) => [event.id, event])), [events]);

  return {
    season,
    seasonUsers,
    matches,
    participants,
    stages,
    events,
    isLoading,
    participantsById,
    stagesById,
    eventsById,
    loadCalendar,
  };
};
